import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";

const CART_KEY = "shoemaster_cart_v1";
const CartContext = createContext(null);

const cartItemKey = (productId, size) => `${productId}::${size || ""}`;

const readCart = () => {
  try {
    const raw = localStorage.getItem(CART_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const writeCart = (items) => {
  try {
    localStorage.setItem(CART_KEY, JSON.stringify(items));
  } catch {
    /* quota — drop silently */
  }
};

export function CartProvider({ children }) {
  const [items, setItems] = useState(readCart);

  useEffect(() => {
    writeCart(items);
  }, [items]);

  // Sync across tabs
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === CART_KEY) setItems(readCart());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const addItem = useCallback((product, { size = null, quantity = 1 } = {}) => {
    setItems((prev) => {
      const key = cartItemKey(product.id, size);
      const idx = prev.findIndex((it) => cartItemKey(it.productId, it.size) === key);
      if (idx >= 0) {
        const next = prev.slice();
        next[idx] = { ...next[idx], quantity: Math.min(99, next[idx].quantity + quantity) };
        return next;
      }
      return [
        ...prev,
        {
          productId: product.id,
          productName: product.name,
          brand: product.brand,
          image: product.image || product.posterUrl || null,
          videoUrl: product.mediaType === "video" ? product.videoUrl : null,
          salePrice: product.salePrice,
          retailPrice: product.retailPrice,
          quantity: Math.min(99, Math.max(1, quantity)),
          size,
          eventId: product.eventId,
          eventTitle: product.eventTitle || "",
        },
      ];
    });
  }, []);

  const setQuantity = useCallback((productId, size, qty) => {
    setItems((prev) => {
      const key = cartItemKey(productId, size);
      return prev
        .map((it) =>
          cartItemKey(it.productId, it.size) === key
            ? { ...it, quantity: Math.min(99, Math.max(1, qty)) }
            : it
        )
        .filter((it) => it.quantity > 0);
    });
  }, []);

  const removeItem = useCallback((productId, size) => {
    setItems((prev) =>
      prev.filter((it) => cartItemKey(it.productId, it.size) !== cartItemKey(productId, size))
    );
  }, []);

  const clearCart = useCallback(() => setItems([]), []);

  const totals = useMemo(() => {
    const itemsCount = items.reduce((s, it) => s + it.quantity, 0);
    const itemsTotal = items.reduce((s, it) => s + it.salePrice * it.quantity, 0);
    const retailTotal = items.reduce(
      (s, it) => s + (it.retailPrice || it.salePrice) * it.quantity,
      0
    );
    const discount = Math.max(0, retailTotal - itemsTotal);
    return {
      itemsCount,
      itemsTotal: Math.round(itemsTotal * 100) / 100,
      retailTotal: Math.round(retailTotal * 100) / 100,
      discount: Math.round(discount * 100) / 100,
    };
  }, [items]);

  const value = useMemo(
    () => ({ items, addItem, setQuantity, removeItem, clearCart, ...totals }),
    [items, addItem, setQuantity, removeItem, clearCart, totals]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
}
